// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Batch filing (design doc §8.5, US-A-07).
 *
 * A page of its own rather than a mode of the filing workbench. The workbench is built
 * around one machine — a scanner in the operator's hand, a draft saved per device number —
 * and batch filing is the opposite job: a file prepared at a desk, read in one pass,
 * committed for up to five hundred machines at once. Sharing a screen would mean one of
 * the two explaining constantly which half applies.
 *
 * **Preflight, then commit, and nothing in between.** The upload only produces a report;
 * the report is what the operator reads and what the commit refers back to. A batch is all
 * or nothing (§8.5): one machine missing one serial stops the file, and there is
 * deliberately no "file the rest" button — a half-filed batch leaves someone reconciling a
 * spreadsheet against a system that no longer matches it.
 *
 * **Modules** (component design §9.9). A robot's module slots are rows like any slot, with
 * the module's SN for a serial. A module batch — 「模组」 at the top — files modules
 * instead: choose the BOM and the module type, fill in a label per module, and the result
 * gives each label its module SN and archive number, to download or print as labels.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { newIdempotencyKey } from "@/api/client";
import { saveResponseAsFile } from "@/api/download";
import { api } from "@/api/endpoints";
import type { ArchiveBatchCommit, ArchivePreflight } from "@/api/types";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { ErrorBanner } from "@/components/ErrorBanner";
import { Pager } from "@/components/Pager";
import { PrintLabelsButton } from "@/components/SnCard";
import { useAvailableBoms } from "@/hooks/useAvailableBoms";
import { useLabelPrinting } from "@/hooks/useLabelPrinting";
import { printLabels } from "@/labels/printLabels";

const TEMPLATE_FILENAME = "cybertron-archive-template.xlsx";
const MAX_ISSUES_SHOWN = 50;

type BatchKind = "ROBOT" | "MODULE";

export function ArchiveImport() {
  const { t, i18n } = useTranslation();
  // Labels print in production only; the batch result still lists every SN.
  const labelPrinting = useLabelPrinting();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [kind, setKind] = useState<BatchKind>("ROBOT");
  const [productTypeId, setProductTypeId] = useState("");
  const [bomId, setBomId] = useState("");
  const [moduleType, setModuleType] = useState("");
  const isModule = kind === "MODULE";
  const [preflight, setPreflight] = useState<ArchivePreflight | null>(null);
  const [filed, setFiled] = useState<ArchiveBatchCommit | null>(null);

  const productTypes = useQuery({
    queryKey: ["product-types", "ACTIVE"],
    queryFn: () => api.productTypes.list({ status: "ACTIVE", limit: 200 }),
  });
  // Only types that use a BOM: §8.5 accepts no others, and offering one here would send
  // the operator to prepare a file the server will refuse.
  const bomTypes = (productTypes.data?.items ?? []).filter((type) => type.has_bom);

  const boms = useAvailableBoms(productTypeId);
  const bomModules = useQuery({
    queryKey: ["bom", bomId, "modules"],
    queryFn: () => api.boms.modules(bomId),
    enabled: isModule && Boolean(bomId),
  });
  const moduleTypes = useQuery({
    queryKey: ["module-types"],
    queryFn: api.moduleTypes.list,
    enabled: isModule,
  });
  const typeLabel = (code: string) =>
    moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US");

  const downloadTemplate = useMutation({
    // Filled in from the BOM when one is chosen: a row per slot of one robot, or of one
    // module for a module batch.
    mutationFn: () =>
      api.archiveImports.template({
        bom_id: bomId || undefined,
        module_type: isModule ? moduleType || undefined : undefined,
      }),
    onSuccess: (response) => saveResponseAsFile(response, TEMPLATE_FILENAME),
  });

  const runPreflight = useMutation({
    mutationFn: (file: File) =>
      api.archiveImports.preflight(productTypeId, bomId, file, isModule ? moduleType : undefined),
    onSuccess: (report) => {
      setFiled(null);
      setPreflight(report);
    },
  });

  // Held for the whole attempt, not minted per click: if the response to a commit is lost,
  // the retry has to be the same request or five hundred machines are filed twice (§7.7).
  const commitKey = useRef(newIdempotencyKey());
  const commit = useMutation({
    mutationFn: (batchId: string) => api.archiveImports.commit(batchId, commitKey.current),
    onSuccess: (result) => {
      setPreflight(null);
      setFiled(result);
      commitKey.current = newIdempotencyKey();
      if (fileInput.current) fileInput.current.value = "";
    },
  });

  // Where the operator is: 1 choose a type, 2 fill the template, 3 upload (which runs the
  // preflight), 4 file. A failed preflight sends them back to 3 with the file fixed.
  // A module batch knows its BOM and type before the file: they say what each label is.
  const ready = Boolean(productTypeId) && (!isModule || Boolean(bomId && moduleType));
  const step = !ready ? 1 : filed ? 5 : !preflight ? 2 : preflight.status === "VALIDATED" ? 4 : 3;
  const stepClass = (n: number) => (n === step ? "is-current" : n < step ? "is-done" : "");
  const filedModules = Boolean(filed?.archives.some((archive) => archive.module_sn));

  return (
    <section>
      <h1>{t("archiveImport.title")}</h1>

      <ol className="steps" aria-label={t("archiveImport.stepsLabel")}>
        <li className={stepClass(1)} aria-current={step === 1 ? "step" : undefined}>
          {t("archiveImport.step1")}
        </li>
        <li className={stepClass(2)} aria-current={step === 2 ? "step" : undefined}>
          {t("archiveImport.step2")}
        </li>
        <li className={stepClass(3)} aria-current={step === 3 ? "step" : undefined}>
          {t("archiveImport.step3")}
        </li>
        <li className={stepClass(4)} aria-current={step === 4 ? "step" : undefined}>
          {t("archiveImport.step4")}
        </li>
      </ol>

      <div className="chip-group" role="group" aria-label={t("archive.kind")}>
        {(["ROBOT", "MODULE"] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={kind === option ? "chip chip-on" : "chip"}
            aria-pressed={kind === option}
            onClick={() => {
              setKind(option);
              setModuleType("");
              setPreflight(null);
              setFiled(null);
            }}
          >
            {t(option === "MODULE" ? "archive.kindModule" : "archive.kindRobot")}
          </button>
        ))}
      </div>

      <div className="filters">
        <select
          aria-label={t("archive.productType")}
          value={productTypeId}
          onChange={(event) => {
            setProductTypeId(event.target.value);
            setBomId("");
            setPreflight(null);
          }}
        >
          <option value="">{t("archive.productType")}</option>
          {bomTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>

        <select
          aria-label={t("archiveImport.defaultBom")}
          value={bomId}
          disabled={!productTypeId}
          onChange={(event) => {
            setBomId(event.target.value);
            setPreflight(null);
          }}
        >
          {/* The empty option is not "none": it means the file says which version each
              machine is built to, which is how a file covering two versions is filed. A
              module batch names its BOM, which says what its modules are. */}
          <option value="">{isModule ? "BOM" : t("archiveImport.bomFromFile")}</option>
          {boms.items.map((bom) => (
            <option key={bom.id} value={bom.id}>
              {/* As on the filing screen: the name in brackets, left out when there is none. */}
              {bom.version}
              {bom.name ? ` (${bom.name})` : ""}
            </option>
          ))}
        </select>

        {isModule && (
          <select
            aria-label={t("moduleFiling.moduleType")}
            value={moduleType}
            disabled={!bomId}
            onChange={(event) => {
              setModuleType(event.target.value);
              setPreflight(null);
            }}
          >
            <option value="">{t("moduleFiling.moduleType")}</option>
            {(bomModules.data?.modules ?? []).map((module) => (
              <option key={module.module_type} value={module.module_type ?? ""}>
                {typeLabel(module.module_type ?? "")}
              </option>
            ))}
          </select>
        )}

        <Pager
          hasNextPage={boms.hasNextPage}
          isFetchingNextPage={boms.isFetchingNextPage}
          onMore={() => void boms.fetchNextPage()}
        />

        <input
          ref={fileInput}
          type="file"
          className="visually-hidden"
          tabIndex={-1}
          aria-hidden="true"
          accept=".xlsx,.csv"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) runPreflight.mutate(file);
            // Cleared so choosing the same file twice fires a change the second time —
            // which is what someone does after fixing the spreadsheet in place.
            event.target.value = "";
          }}
        />
        <button
          type="button"
          disabled={downloadTemplate.isPending}
          onClick={() => downloadTemplate.mutate()}
        >
          {t("bom.downloadTemplate")}
        </button>
        {/* The step the page is waiting on is the one in the accent colour. */}
        <button
          type="button"
          className={step === 2 || step === 3 ? "button-primary" : undefined}
          disabled={!ready || runPreflight.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {runPreflight.isPending ? t("common.loading") : t("archiveImport.chooseFile")}
        </button>
      </div>
      <p className="hint">
        {isModule
          ? t(ready ? "archiveImport.moduleHint" : "archiveImport.needsModuleType")
          : productTypeId
            ? t("archiveImport.hint")
            : t("archiveImport.needsType")}
      </p>

      <ErrorBanner
        error={runPreflight.error ?? commit.error ?? downloadTemplate.error ?? boms.error}
      />

      {preflight && (
        <div
          className={`banner ${preflight.status === "VALIDATED" ? "banner-info" : "banner-error"}`}
        >
          <h2>{t("archiveImport.reportTitle")}</h2>
          {preflight.status === "VALIDATED" ? (
            <>
              <p>
                {t("archiveImport.passed", {
                  devices: preflight.device_count,
                  serials: preflight.serial_count,
                })}
              </p>
              <button
                type="button"
                className="button-primary"
                disabled={commit.isPending}
                onClick={() => commit.mutate(preflight.batch_id)}
              >
                {commit.isPending ? t("common.loading") : t("archiveImport.commit")}
              </button>
            </>
          ) : (
            <>
              {/* No "file the good ones": §8.5 is explicit, and the count is the whole
                  point — a report saying "3 problems" is a fix list, not a verdict. */}
              <p>{t("archiveImport.failed", { count: preflight.issues.length })}</p>
              <table>
                <thead>
                  <tr>
                    <th>{t("bom.issueRow")}</th>
                    <th>{t("bom.issueColumn")}</th>
                    <th>{t("bom.issueCode")}</th>
                    <th>{t("archiveImport.issueDetail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {preflight.issues.slice(0, MAX_ISSUES_SHOWN).map((issue, index) => (
                    <tr key={index}>
                      <td>{issue.row ?? "—"}</td>
                      <td>{issue.column_label ?? "—"}</td>
                      {/* The code stays on hover: it is what support searches for. */}
                      <td title={issue.code}>{issue.message}</td>
                      <td className="mono">{issue.value ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preflight.issues.length > MAX_ISSUES_SHOWN && (
                <p className="hint">+{preflight.issues.length - MAX_ISSUES_SHOWN}</p>
              )}
            </>
          )}
        </div>
      )}

      {filed && (
        <div className="banner banner-info">
          <h2>{t("archiveImport.filedTitle", { count: filed.device_count })}</h2>
          {/* The numbers, linked: they are what the operator writes on the machines, and
              the archive number is only assigned here. */}
          {filedModules ? (
            <>
              {/* What the labels are printed from: each label of the file, its new SN. */}
              <div className="button-group">
                <button type="button" onClick={() => downloadResult(filed)}>
                  {t("archiveImport.downloadResult")}
                </button>
                <PrintLabelsButton
                  printable={labelPrinting.printable}
                  onPrint={() =>
                    printLabels(
                      filed.archives.map((archive) => ({
                        sn: archive.module_sn ?? "",
                        symbology: "code128" as const,
                        lines: [archive.archive_no, archive.label ?? ""].filter(Boolean),
                      })),
                      t("robotSn.print"),
                    )
                  }
                />
              </div>
              <table>
                <thead>
                  <tr>
                    <th>{t("archiveImport.label")}</th>
                    <th>{t("moduleFiling.snLabel")}</th>
                    <th>{t("archive.archiveNo")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filed.archives.map((archive) => (
                    <tr key={archive.archive_id}>
                      <td>{archive.label}</td>
                      <td className="mono">{archive.module_sn}</td>
                      <td className="mono">
                        <Link to={`/archives/${archive.archive_id}`}>{archive.archive_no}</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <>
              {/* The robots' SNs were generated with the batch: their QR labels print
                  from here. */}
              {filed.archives.some((archive) => archive.robot_sn) && (
                <div className="button-group">
                  <PrintLabelsButton
                    printable={labelPrinting.printable}
                    onPrint={() =>
                      printLabels(
                        filed.archives
                          .filter((archive) => archive.robot_sn)
                          .map((archive) => ({
                            sn: archive.robot_sn ?? "",
                            symbology: "qr" as const,
                            lines: [archive.archive_no, archive.device_no ?? ""].filter(Boolean),
                          })),
                        t("robotSn.print"),
                      )
                    }
                  />
                </div>
              )}
              <table>
                <thead>
                  <tr>
                    <th>{t("archive.archiveNo")}</th>
                    <th>{t("archive.device")}</th>
                    <th>{t("robotSn.label")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filed.archives.map((archive) => (
                    <tr key={archive.archive_id}>
                      <td className="mono">
                        <Link to={`/archives/${archive.archive_id}`}>{archive.archive_no}</Link>
                      </td>
                      <td className="mono">{archive.device_no}</td>
                      <td className="mono">{archive.robot_sn ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/** The module batch's result as CSV — label, module SN, archive number — for the label
 *  printer's own software, or to keep with the paperwork. */
function downloadResult(filed: ArchiveBatchCommit) {
  const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const lines = [
    ["label", "module_sn", "archive_no"],
    ...filed.archives.map((a) => [a.label ?? "", a.module_sn ?? "", a.archive_no]),
  ].map((row) => row.map(quote).join(","));
  const blob = new Blob([`\ufeff${lines.join("\r\n")}\r\n`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `modules-${filed.batch_id}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
